import Phaser from "phaser";
import { GameState } from "../state/GameState";
import { submitScore, formatTime } from "../net/leaderboard";
import { makeOverlay } from "../ui/leaderboardUi";

const NAME_MAX = 16;

/**
 * Shown after the Mallorca ending. Displays the player's run totals (points,
 * collectibles, sheepies, time), takes a name, and submits to the leaderboard.
 * On success it hands off to the Leaderboard scene with the saved row's id so it
 * can be highlighted. Skipping opens the leaderboard read-only.
 *
 * Rendered as a DOM overlay (see leaderboardUi.ts) over a dark Phaser backdrop -
 * the same approach passwordGate.ts uses for a text field.
 */
export class SubmitScoreScene extends Phaser.Scene {
  private overlay?: HTMLDivElement;

  constructor() {
    super("SubmitScore");
  }

  create() {
    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height / 2, width, height, 0x0e0f13);

    // The gameplay scenes capture WASD / arrows / space / F for movement, and Phaser
    // calls preventDefault on those keys. The captures live on the global keyboard
    // manager and persist after a level ends, so without this they would swallow
    // W/A/S/D/F/space in the name field below. Clear them so every key reaches the
    // DOM input; levels re-add their own captures when they start.
    this.input.keyboard?.clearCaptures();

    const points = GameState.totalPoints();
    const collectibles = GameState.totalCollectibles();
    const sheepies = GameState.sheepyCount();
    const timeStr = formatTime(GameState.totalTimeMs());

    const overlay = makeOverlay();
    this.overlay = overlay;
    overlay.innerHTML = `
      <form class="lb-card" novalidate>
        <div class="lb-title">You made it to Mallorca!</div>
        <div class="lb-sub">Enter your name to join the leaderboard.</div>
        <div class="lb-stats">
          <div class="lb-stat"><span>Points</span><b>${points}</b></div>
          <div class="lb-stat"><span>Collectibles</span><b>${collectibles}</b></div>
          <div class="lb-stat"><span>Sheepies</span><b>${sheepies} / 3</b></div>
          <div class="lb-stat"><span>Time</span><b>${timeStr}</b></div>
        </div>
        <input class="lb-input" type="text" maxlength="${NAME_MAX}" placeholder="Your name"
               aria-label="Your name" autocomplete="off" spellcheck="false" />
        <div class="lb-error" role="alert" hidden></div>
        <div class="lb-actions">
          <button class="lb-btn lb-btn-secondary" type="button" data-skip>Skip</button>
          <button class="lb-btn" type="submit" data-submit>Submit score</button>
        </div>
      </form>
    `;

    const form = overlay.querySelector<HTMLFormElement>(".lb-card")!;
    const input = overlay.querySelector<HTMLInputElement>(".lb-input")!;
    const error = overlay.querySelector<HTMLDivElement>(".lb-error")!;
    const submitBtn = overlay.querySelector<HTMLButtonElement>("[data-submit]")!;
    const skipBtn = overlay.querySelector<HTMLButtonElement>("[data-skip]")!;

    input.focus();

    const showError = (msg: string) => {
      error.textContent = msg;
      error.hidden = false;
    };

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const name = input.value.trim();
      if (!name) {
        showError("Please enter a name to submit.");
        input.focus();
        return;
      }
      error.hidden = true;
      submitBtn.disabled = true;
      skipBtn.disabled = true;
      submitBtn.textContent = "Saving...";
      try {
        const { savedId } = await submitScore({
          name,
          points,
          collectibles,
          sheepies,
          timeMs: GameState.totalTimeMs(),
        });
        // Success: the input value and stats are no longer needed - move on.
        this.scene.start("Leaderboard", { savedId });
      } catch {
        // Failure keeps the entered name and stats on screen so the player can retry
        // (or fall back to sending their score to Matthew on Slack).
        submitBtn.disabled = false;
        skipBtn.disabled = false;
        submitBtn.textContent = "Retry";
        showError("Leaderboard error. Send your score to Matthew on Slack.");
        input.focus();
      }
    });

    skipBtn.addEventListener("click", () => this.scene.start("Leaderboard", {}));

    // Tear the overlay down whenever this scene stops, so it never lingers over
    // another scene.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  private teardown() {
    this.overlay?.remove();
    this.overlay = undefined;
  }
}
